import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

const WizardStep = ({ title, description, children, onNext, onPrev, onSkip, isLast, isLoading, isFirst }) => (
    <div className="nemtly-wizard-step">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
        <div className="nemtly-wizard-body">
            {children}
        </div>
        <div className="nemtly-wizard-footer">
            <div className="nemtly-wizard-footer-left">
                {!isFirst && !isLast && (
                    <button
                        className="components-button is-secondary"
                        onClick={onPrev}
                        disabled={isLoading}
                    >
                        {__('Back', 'nemtly-booking')}
                    </button>
                )}
                {!isLast && (
                    <button
                        className="components-button is-link"
                        onClick={onSkip}
                        disabled={isLoading}
                    >
                        {__('Skip this step', 'nemtly-booking')}
                    </button>
                )}
            </div>
            <button
                className="components-button is-primary"
                onClick={onNext}
                disabled={isLoading}
            >
                {isLoading ? __('Processing...', 'nemtly-booking') : (isLast ? __('Go to Dashboard', 'nemtly-booking') : __('Continue', 'nemtly-booking'))}
            </button>
        </div>
    </div>
);

const SetupWizard = () => {
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [settings, setSettings] = useState({
        business_persona: 'other',
        business_name: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        availability: {
            monday: { active: true, start: '09:00', end: '17:00' },
            tuesday: { active: true, start: '09:00', end: '17:00' },
            wednesday: { active: true, start: '09:00', end: '17:00' },
            thursday: { active: true, start: '09:00', end: '17:00' },
            friday: { active: true, start: '09:00', end: '17:00' },
            saturday: { active: true, start: '09:00', end: '17:00' },
            sunday: { active: true, start: '09:00', end: '17:00' },
        }
    });

    const updateSetting = (key, value) => {
        setSettings({ ...settings, [key]: value });
    };

    const handleNext = () => setStep(step + 1);
    const handlePrev = () => setStep(step - 1);
    const handleSkip = () => setStep(step + 1);

    const saveSettings = async () => {
        setIsLoading(true);
        try {
            await apiFetch({
                path: '/nemtly-booking/v1/setup/settings',
                method: 'POST',
                data: { general: settings },
            });
            handleNext();
        } catch (error) {
            alert(__('Error saving settings.', 'nemtly-booking'));
        } finally {
            setIsLoading(false);
        }
    };

    const completeSetup = async () => {
        setIsLoading(true);
        try {
            await apiFetch({
                path: '/nemtly-booking/v1/setup/complete',
                method: 'POST',
            });
            window.location.href = 'admin.php?page=nemtly-booking';
        } catch (error) {
            alert(__('Error completing setup.', 'nemtly-booking'));
            setIsLoading(false);
        }
    };

    const totalSteps = 4;
    const progress = (step / totalSteps) * 100;

    return (
        <div className="nemtly-setup-wizard-wrapper">
            <div className="nemtly-wizard-header">
                <h1>{__('Set up Nemtly', 'nemtly-booking')}</h1>
                <div className="nemtly-wizard-progress-container">
                    <div
                        className="nemtly-wizard-progress-bar"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
            </div>

            <div className="nemtly-wizard-content">
                { /* Step 1: Persona */}
                {step === 1 && (
                    <WizardStep
                        title={__('What best describes you?', 'nemtly-booking')}
                        description={__('We will tailor your experience based on your business type.', 'nemtly-booking')}
                        onNext={handleNext}
                        onSkip={handleSkip}
                        isFirst={true}
                    >
                        <div className="persona-grid">
                            {[
                                { id: 'tutor', title: __('Tutor / Coach', 'nemtly-booking'), desc: __('Individual teaching and sessions', 'nemtly-booking') },
                                { id: 'studio', title: __('Studio / Gym', 'nemtly-booking'), desc: __('Classes and group bookings', 'nemtly-booking') },
                                { id: 'professional', title: __('Consultant', 'nemtly-booking'), desc: __('Meetings and appointments', 'nemtly-booking') },
                                { id: 'other', title: __('Something Else', 'nemtly-booking'), desc: __('Custom booking needs', 'nemtly-booking') },
                            ].map(p => (
                                <div
                                    key={p.id}
                                    className={`persona-card ${settings.business_persona === p.id ? 'is-active' : ''}`}
                                    onClick={() => updateSetting('business_persona', p.id)}
                                >
                                    <h3>{p.title}</h3>
                                    <p>{p.desc}</p>
                                </div>
                            ))}
                        </div>
                    </WizardStep>
                )}

                { /* Step 2: Identity */}
                {step === 2 && (
                    <WizardStep
                        title={__('Business Identity', 'nemtly-booking')}
                        description={__('Tell us a bit about your brand.', 'nemtly-booking')}
                        onNext={saveSettings}
                        onPrev={handlePrev}
                        onSkip={handleSkip}
                        isLoading={isLoading}
                    >
                        <div className="nemtly-form-group">
                            <label>{__('Business Name', 'nemtly-booking')}</label>
                            <input
                                type="text"
                                className="components-text-control__input"
                                value={settings.business_name}
                                onChange={(e) => updateSetting('business_name', e.target.value)}
                                placeholder={__('e.g. Acme Studio', 'nemtly-booking')}
                            />
                        </div>
                        <div className="nemtly-form-group">
                            <label>{__('Timezone', 'nemtly-booking')}</label>
                            <select
                                className="components-select-control__input"
                                value={settings.timezone}
                                onChange={(e) => updateSetting('timezone', e.target.value)}
                            >
                                <option value="UTC">UTC</option>
                                <option value="America/New_York">New York</option>
                                <option value="Europe/London">London</option>
                                {/* In a real app we'd fetch all TZs or use a searchable component */}
                            </select>
                        </div>
                    </WizardStep>
                )}

                { /* Step 3: Availability */}
                {step === 3 && (
                    <WizardStep
                        title={__('Work Hours', 'nemtly-booking')}
                        description={__('When can people book your services?', 'nemtly-booking')}
                        onNext={handleNext}
                        onPrev={handlePrev}
                        onSkip={handleSkip}
                    >
                        <div className="nb-space-y-4">
                            {Object.entries(settings.availability).filter(([day]) => !['saturday', 'sunday'].includes(day)).map(([day, config]) => (
                                <div key={day} className="nb-flex nb-items-center nb-justify-between nb-p-3 nb-border nb-border-slate-100 nb-rounded-lg">
                                    <span className="nb-capitalize nb-font-medium">{day}</span>
                                    <div className="nb-flex nb-items-center nb-gap-3">
                                        <input type="time" value={config.start} readOnly className="nb-p-1 nb-border nb-rounded" />
                                        <span>-</span>
                                        <input type="time" value={config.end} readOnly className="nb-p-1 nb-border nb-rounded" />
                                    </div>
                                </div>
                            ))}
                            <p className="nb-text-xs nb-text-slate-500 nb-italic">
                                {__('Standard 9:00 AM - 5:00 PM applied. You can refine this later.', 'nemtly-booking')}
                            </p>
                        </div>
                    </WizardStep>
                )}

                { /* Step 4: Finish */}
                {step === 4 && (
                    <WizardStep
                        title={__('You\'re all set!', 'nemtly-booking')}
                        description={__('Welcome to the Nemtly family. Your booking engine is ready.', 'nemtly-booking')}
                        onNext={completeSetup}
                        onPrev={handlePrev}
                        isLast={true}
                        isLoading={isLoading}
                    >
                        <div className="nb-space-y-4">
                            <div className="nb-flex nb-items-center nb-gap-3 nb-text-emerald-600">
                                <span>✅</span> <span>{__('Core architecture initialized', 'nemtly-booking')}</span>
                            </div>
                            <div className="nb-flex nb-items-center nb-gap-3 nb-text-emerald-600">
                                <span>✅</span> <span>{__('Business profile configured', 'nemtly-booking')}</span>
                            </div>
                            <div className="nb-flex nb-items-center nb-gap-3 nb-text-emerald-600">
                                <span>✅</span> <span>{__('Ready to accept bookings', 'nemtly-booking')}</span>
                            </div>
                        </div>
                    </WizardStep>
                )}
            </div>
        </div>
    );
};

export default SetupWizard;

